import { useMutation, useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../api/client";

interface Cooperatie {
  id: string;
  naam: string;
  werksoort: string | null;
}
interface Comaker {
  id: string;
  naam: string;
}
interface Template {
  id: string;
  name: string;
  description: string;
  version: string;
}
interface Werkpakket {
  werkpakket: string;
  aantal_projecten: number;
}
interface Project {
  id: string;
  naam: string;
  werkpakket: string | null;
}

export default function GenerateReportPage() {
  const navigate = useNavigate();
  const [templateId, setTemplateId] = useState("rgs_samenwerking");
  const [coopId, setCoopId] = useState<string>("");
  const [comakerIds, setComakerIds] = useState<string[]>([]);
  const [werkpakketten, setWerkpakketten] = useState<string[]>([]);
  const [projectIds, setProjectIds] = useState<string[]>([]);
  const [projectZoek, setProjectZoek] = useState("");

  const templates = useQuery({
    queryKey: ["templates"],
    queryFn: async () => (await api.get<Template[]>("/templates")).data,
  });
  const coops = useQuery({
    queryKey: ["cooperaties"],
    queryFn: async () => (await api.get<Cooperatie[]>("/cooperaties")).data,
  });
  // Gebruikers met toegang tot precies één coöperatie hoeven niet te kiezen.
  useEffect(() => {
    if (!coopId && coops.data?.length === 1) setCoopId(coops.data[0].id);
  }, [coopId, coops.data]);
  const comakers = useQuery({
    queryKey: ["comakers", coopId],
    queryFn: async () =>
      (await api.get<Comaker[]>("/comakers", { params: { cooperatie_id: coopId } })).data,
    enabled: !!coopId,
  });
  const werkpakkettenQuery = useQuery({
    queryKey: ["werkpakketten", coopId],
    queryFn: async () =>
      (await api.get<Werkpakket[]>("/werkpakketten", { params: { cooperatie_id: coopId } })).data,
    enabled: !!coopId,
  });
  // Projectenlijst alleen relevant voor het Planning-overzicht (detail-selectie).
  // Filtert mee op de gekozen co-makers en werkpakketten (zelfde scope als het rapport).
  const isPlanning = templateId === "planning_overzicht";
  // KPI Trends & Verschillen vergelijkt exact 2 jaartallen (werkpakketten).
  const isKpiTrends = templateId === "kpi_trends_verschillen";
  const projectsQuery = useQuery({
    queryKey: ["projects", coopId, comakerIds, werkpakketten],
    queryFn: async () =>
      (
        await api.get<Project[]>("/projects", {
          params: {
            cooperatie_id: coopId,
            ...(comakerIds.length ? { comaker_ids: comakerIds.join(",") } : {}),
            ...(werkpakketten.length ? { werkpakketten: werkpakketten.join(",") } : {}),
          },
        })
      ).data,
    enabled: !!coopId && isPlanning,
  });

  // Verwijder geselecteerde projecten die door een filterwijziging buiten scope vallen.
  useEffect(() => {
    if (!projectsQuery.data) return;
    const ids = new Set(projectsQuery.data.map((p) => p.id));
    setProjectIds((prev) => {
      const next = prev.filter((id) => ids.has(id));
      return next.length === prev.length ? prev : next;
    });
  }, [projectsQuery.data]);

  // Bij wisselen naar KPI Trends: trim een eventueel langere selectie tot 2.
  useEffect(() => {
    if (isKpiTrends) setWerkpakketten((prev) => (prev.length > 2 ? prev.slice(0, 2) : prev));
  }, [isKpiTrends]);

  const generate = useMutation({
    mutationFn: async () => {
      const r = await api.post<{ report_id: string; status: string }>("/reports/generate", {
        template_id: templateId,
        cooperatie_id: coopId,
        comaker_ids: comakerIds,
        werkpakketten,
        project_ids: isPlanning ? projectIds : [],
        filters: {},
      });
      return r.data;
    },
    onSuccess: (data) => navigate(`/rapport/${data.report_id}`),
  });

  const toggleComaker = (id: string) =>
    setComakerIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  const toggleWerkpakket = (wp: string) =>
    setWerkpakketten((prev) => {
      if (prev.includes(wp)) return prev.filter((x) => x !== wp);
      // KPI Trends: max 2 jaartallen; verdere keuzes worden geblokkeerd.
      if (isKpiTrends && prev.length >= 2) return prev;
      return [...prev, wp];
    });
  const toggleProject = (id: string) =>
    setProjectIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const projectenGefilterd = (projectsQuery.data ?? []).filter((p) =>
    p.naam.toLowerCase().includes(projectZoek.trim().toLowerCase()),
  );

  // KPI Trends vereist exact 2 gekozen jaartallen.
  const werkpakketOk = !isKpiTrends || werkpakketten.length === 2;
  const canSubmit = !!templateId && !!coopId && !generate.isPending && werkpakketOk;

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-kbs-primary">Rapport genereren</h1>

      <div className="space-y-5 rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
        <Field label="Template">
          <select
            className="w-full rounded border border-slate-300 px-3 py-2 text-sm"
            value={templateId}
            onChange={(e) => setTemplateId(e.target.value)}
          >
            {templates.data?.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name} — {t.description}
              </option>
            ))}
          </select>
        </Field>

        <Field label="Coöperatie">
          {coops.isLoading ? (
            <p className="text-sm text-slate-500">Laden…</p>
          ) : (
            <select
              className="w-full rounded border border-slate-300 px-3 py-2 text-sm"
              value={coopId}
              onChange={(e) => {
                setCoopId(e.target.value);
                setComakerIds([]);
                setWerkpakketten([]);
                setProjectIds([]);
                setProjectZoek("");
              }}
            >
              <option value="">— Kies een Coöperatie —</option>
              {coops.data?.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.naam}
                </option>
              ))}
            </select>
          )}
        </Field>

        <Field label="Co-makers (optioneel — leeg = alle)">
          {!coopId && <p className="text-sm text-slate-400">Kies eerst een Coöperatie.</p>}
          {coopId && comakers.isLoading && <p className="text-sm text-slate-500">Laden…</p>}
          {comakers.data && (
            <div className="grid grid-cols-2 gap-2">
              {comakers.data.map((c) => (
                <label
                  key={c.id}
                  className="flex cursor-pointer items-center gap-2 rounded border border-slate-200 px-3 py-2 text-sm hover:bg-slate-50"
                >
                  <input
                    type="checkbox"
                    checked={comakerIds.includes(c.id)}
                    onChange={() => toggleComaker(c.id)}
                  />
                  <span>{c.naam}</span>
                </label>
              ))}
            </div>
          )}
        </Field>

        <Field
          label={
            isKpiTrends
              ? "Werkpakketten / jaren (kies exact 2 jaartallen om te vergelijken)"
              : "Werkpakketten / jaren (optioneel — leeg = alle jaren)"
          }
        >
          {isKpiTrends && (
            <p
              className={
                "mb-2 text-sm " +
                (werkpakketten.length === 2 ? "text-slate-500" : "text-amber-600")
              }
            >
              {werkpakketten.length === 2
                ? `Vergelijking: ${[...werkpakketten].sort().join(" vs ")}`
                : `Kies precies 2 jaartallen (${werkpakketten.length}/2 gekozen).`}
            </p>
          )}
          {!coopId && <p className="text-sm text-slate-400">Kies eerst een Coöperatie.</p>}
          {coopId && werkpakkettenQuery.isLoading && (
            <p className="text-sm text-slate-500">Laden…</p>
          )}
          {werkpakkettenQuery.data && werkpakkettenQuery.data.length === 0 && (
            <p className="text-sm text-slate-400">
              Geen werkpakket-jaartallen aanwezig voor deze Coöperatie.
            </p>
          )}
          {werkpakkettenQuery.data && werkpakkettenQuery.data.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {werkpakkettenQuery.data.map((w) => {
                const active = werkpakketten.includes(w.werkpakket);
                return (
                  <button
                    type="button"
                    key={w.werkpakket}
                    onClick={() => toggleWerkpakket(w.werkpakket)}
                    className={
                      "rounded-full border px-3 py-1 text-sm transition " +
                      (active
                        ? "border-kbs-primary bg-kbs-primary text-white"
                        : "border-slate-300 bg-white text-slate-700 hover:bg-slate-50")
                    }
                  >
                    {w.werkpakket}
                    <span className="ml-1 text-xs opacity-70">({w.aantal_projecten})</span>
                  </button>
                );
              })}
            </div>
          )}
        </Field>

        {isPlanning && (
          <Field label="Projecten voor detailpagina's (optioneel — leeg = alleen totaaloverzicht)">
            {!coopId && <p className="text-sm text-slate-400">Kies eerst een Coöperatie.</p>}
            {coopId && projectsQuery.isLoading && <p className="text-sm text-slate-500">Laden…</p>}
            {coopId && projectsQuery.data && (
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={projectZoek}
                    onChange={(e) => setProjectZoek(e.target.value)}
                    placeholder="Zoek project…"
                    className="w-full rounded border border-slate-300 px-3 py-2 text-sm"
                  />
                  {projectIds.length > 0 && (
                    <button
                      type="button"
                      onClick={() => setProjectIds([])}
                      className="whitespace-nowrap rounded border border-slate-300 px-3 py-2 text-xs text-slate-600 hover:bg-slate-50"
                    >
                      Wis ({projectIds.length})
                    </button>
                  )}
                </div>
                <div className="max-h-64 space-y-1 overflow-y-auto rounded border border-slate-200 p-2">
                  {projectenGefilterd.length === 0 && (
                    <p className="px-2 py-1 text-sm text-slate-400">Geen projecten gevonden.</p>
                  )}
                  {projectenGefilterd.map((p) => (
                    <label
                      key={p.id}
                      className="flex cursor-pointer items-center gap-2 rounded px-2 py-1 text-sm hover:bg-slate-50"
                    >
                      <input
                        type="checkbox"
                        checked={projectIds.includes(p.id)}
                        onChange={() => toggleProject(p.id)}
                      />
                      <span>{p.naam}</span>
                      {p.werkpakket && (
                        <span className="ml-auto text-xs text-slate-400">{p.werkpakket}</span>
                      )}
                    </label>
                  ))}
                </div>
              </div>
            )}
          </Field>
        )}

        {generate.error && (
          <p className="text-sm text-rose-600">
            Fout bij genereren: {(generate.error as Error).message}
          </p>
        )}

        <button
          disabled={!canSubmit}
          onClick={() => generate.mutate()}
          className="rounded bg-kbs-primary px-5 py-2 text-sm font-semibold text-white hover:bg-kbs-dark disabled:opacity-50"
        >
          {generate.isPending ? "Bezig…" : "Genereer rapport"}
        </button>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="mb-1 block text-sm font-medium text-kbs-primary">{label}</label>
      {children}
    </div>
  );
}
